import { oc } from "@orpc/contract";
import { openapi } from "@orpc/openapi";
import { z } from "zod";
import { errorSchema } from "./errors";
import {
  NOTE_EDIT_COMBINED_INSTRUCTION_MAX,
  noteEditOriginSchema,
  noteEditProposalSchema,
} from "./note-edit-proposal";
import { publicIdSchema } from "./public-id";
import { visitSourceIdsSchema } from "./visit-source";

const id = publicIdSchema;
const result = z
  .string()
  .refine(
    (value) => new TextEncoder().encode(value).length <= 65535,
    "Note exceeds 65535 UTF-8 bytes",
  );
export const assistantNoteReviewSchema = z.strictObject({
  // A refined proposal is applied with all its requests, one per line
  // (combinedNoteEditInstruction).
  instruction: z.string().trim().min(1).max(NOTE_EDIT_COMBINED_INSTRUCTION_MAX),
  baseVersion: z.string().regex(/^[0-9a-f]{64}$/),
  acknowledgeFactChanges: z.boolean(),
  // The visit sources the proposal used, so the server re-check sees the
  // same sources (facts the instruction asked to add from them).
  sourceIds: visitSourceIdsSchema.optional(),
  // The proposal's origin, so the server re-check applies the same rules.
  origin: noteEditOriginSchema.optional(),
});
export const assistedNoteConflictSchema = z.discriminatedUnion("reason", [
  z.object({ reason: z.literal("stale_proposal") }),
  z.object({
    reason: z.literal("unconfirmed_fact_changes"),
    factChanges: noteEditProposalSchema.shape.factChanges,
  }),
]);
export const assistedNoteErrorSchema = errorSchema.extend({
  error: errorSchema.shape.error.extend({
    code: z.literal("CONFLICT"),
    data: assistedNoteConflictSchema,
  }),
});
export type AssistantNoteReview = z.output<typeof assistantNoteReviewSchema>;
const assistedContent = z.strictObject({
  result: result
    .max(60000)
    .refine(
      (value) => value.trim().length > 0,
      "An assisted note cannot be empty",
    ),
  assistantReview: assistantNoteReviewSchema,
});
export const visitUpdateSchema = z.union([
  z
    .strictObject({
      title: z
        .string()
        .refine(
          (value) => value.trim().length > 0 && Array.from(value).length <= 128,
          "Title must contain 1 to 128 characters",
        )
        .optional(),
      result: result.optional(),
      assistantReview: z.undefined().optional(),
    })
    .refine(
      (value) => value.title !== undefined || value.result !== undefined,
      "An edit is required",
    ),
  assistedContent,
]);
export const visitNoteUpdateSchema = z.union([
  z.strictObject({ result, assistantReview: z.undefined().optional() }),
  assistedContent,
]);
export type VisitUpdateInput = z.infer<typeof visitUpdateSchema>;
export type VisitNoteUpdateInput = z.infer<typeof visitNoteUpdateSchema>;
const response = z.object({ public_id: publicIdSchema });
const write = oc.errors({
  BAD_REQUEST: { message: "Invalid request" },
  NOT_FOUND: { message: "Not Found" },
  METHOD_NOT_ALLOWED: { message: "Method Not Allowed" },
  INTERNAL_SERVER_ERROR: { message: "Internal Server Error" },
  UNAUTHORIZED: { message: "Authentication required" },
  FORBIDDEN: { message: "Account required" },
  SERVICE_UNAVAILABLE: { message: "Authentication unavailable" },
});
const assistedWrite = write.errors({
  CONFLICT: {
    message: "Note review conflict",
    data: assistedNoteConflictSchema,
  },
});
// Restores an earlier version of the clinical note (from its history) as a
// new version: the note as it is now goes into the history first, so a
// restore can be undone the same way. Both are SHA-256 (hex) of note text:
// `restoreVersion` names the saved version to bring back (the newest one
// with that text), `expectedVersion` the note the client shows as current;
// the restore is refused when the note changed since (e.g. Undo after
// another edit was applied).
const noteHash = z.string().regex(/^[0-9a-f]{64}$/);
export const visitNoteRestoreSchema = z.strictObject({
  restoreVersion: noteHash,
  expectedVersion: noteHash,
});
export type VisitNoteRestoreInput = z.output<typeof visitNoteRestoreSchema>;
export const visitNoteRestoreConflictSchema = z.object({
  reason: z.literal("stale_note"),
});
const restoreWrite = write.errors({
  CONFLICT: {
    message: "The note changed",
    data: visitNoteRestoreConflictSchema,
  },
});
export const visitWriteContract = {
  restoreVisitNote: restoreWrite
    .input(
      z.object({
        params: z.strictObject({ public_id: id }),
        query: z.strictObject({}).default({}),
        body: visitNoteRestoreSchema,
      }),
    )
    .meta(
      openapi({
        method: "POST",
        path: "/api/v2/visits/{public_id}/note/restore",
        inputStructure: "detailed",
        successStatus: 200,
        spec: (spec) => ({ ...spec, security: [{ bearerAuth: [] }] }),
      }),
    )
    .output(response),
  updateVisit: assistedWrite
    .input(
      z.object({
        params: z.strictObject({ public_id: id }),
        query: z.strictObject({}).default({}),
        body: visitUpdateSchema,
      }),
    )
    .meta(
      openapi({
        method: "PATCH",
        path: "/api/v2/visits/{public_id}",
        inputStructure: "detailed",
        spec: (spec) => ({ ...spec, security: [{ bearerAuth: [] }] }),
      }),
    )
    .output(response),
  deleteVisit: write
    .input(
      z.object({
        params: z.strictObject({ public_id: id }),
        query: z.strictObject({}).default({}),
        body: z.undefined().optional(),
      }),
    )
    .meta(
      openapi({
        method: "DELETE",
        path: "/api/v2/visits/{public_id}",
        inputStructure: "detailed",
        spec: (spec) => ({ ...spec, security: [{ bearerAuth: [] }] }),
      }),
    )
    .output(response),
  updateVisitNote: assistedWrite
    .input(
      z.object({
        params: z.strictObject({ public_id: id, notePublicId: id }),
        query: z.strictObject({}).default({}),
        body: visitNoteUpdateSchema,
      }),
    )
    .meta(
      openapi({
        method: "PATCH",
        path: "/api/v2/visits/{public_id}/notes/{notePublicId}",
        inputStructure: "detailed",
        spec: (spec) => ({ ...spec, security: [{ bearerAuth: [] }] }),
      }),
    )
    .output(response),
};
